/**
 * Shared types for the (mocked) client product dashboard.
 *
 * The intended production architecture: a scheduled quarterly job writes ONE `ProjectSnapshot`
 * per project per date into a history table, pulling from GitHub / Aikido / Datadog. Every
 * dashboard is a view over that history — progress series and health trends are just historical
 * rows plotted over time. Here that history is mocked in `snapshots.ts`, and each data source is a
 * separate adapter module (`github.ts`, `aikido.ts`, `datadog.ts`) with a clean interface, so a
 * source can be swapped for a real API without touching the UI.
 */

import type { ProductSecurityIssue } from '@data/utils/types';

export type HealthStatus = 'good' | 'watch' | 'risk';

// Status only drives colour — the dashboard shows objective numbers and lets green/amber/red signal
// good/slipping/bad, rather than a written Panenco verdict.
export const STATUS_META: Record<HealthStatus, { text: string; stroke: string }> = {
  good: { text: 'text-emerald-600 dark:text-emerald-400', stroke: '#10b981' },
  watch: { text: 'text-amber-600 dark:text-amber-400', stroke: '#f59e0b' },
  risk: { text: 'text-red-600 dark:text-red-400', stroke: '#ef4444' },
};

const RANK: Record<HealthStatus, number> = { good: 0, watch: 1, risk: 2 };
export const worstStatus = (statuses: HealthStatus[]): HealthStatus =>
  statuses.reduce<HealthStatus>((worst, s) => (RANK[s] > RANK[worst] ? s : worst), 'good');

export interface SeveritySplit {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface FreshnessBuckets {
  active: number; // committed to recently
  inactive: number; // quiet for a while
  dormant: number; // no commits in a long time
}

/** One row per project per date — the unit the snapshot job writes and every view reads. */
// ---- View-model the UI consumes (adapters build this from the snapshot history) ----

export interface TrendPoint {
  label: string;
  value: number;
  display: string;
  status: HealthStatus;
  /** A day with nothing to plot: it keeps its place on the axis and breaks the line, its value unread. */
  gap?: boolean;
}

export interface HealthMetric {
  status: HealthStatus;
  value: string;
  detail?: string; // optional tangible sub-line, e.g. "24,850 of 24,900 sessions"
}

// Response time carries a plain-language verdict + the raw ms, so the card can show a Fast/Moderate/
// Slow scale — a number alone ("410 ms") doesn't tell a client whether that's good.
export interface ResponseTimeMetric {
  status: HealthStatus;
  value: string; // formatted, e.g. "380 ms" / "1.4 s"
  verdict: string; // "Fast" | "Moderate" | "Slow"
  ms: number; // raw, for positioning the marker on the scale
  series: TrendPoint[]; // one point per day over Sentry's retention window, for the over-time line chart
}

export interface CrashFreeMetric extends HealthMetric {
  series: TrendPoint[]; // one point per day with enough sessions, for the over-time line chart
}

// A warm heat ramp — every severity reads as "issue" (no neutral grey).
export const SEVERITY_COLORS = { critical: '#b91c1c', high: '#ef4444', medium: '#f59e0b', low: '#eab308' };

/** An issue as the card lists it: Aikido's fields, with the check and the severity named. */
export interface SecurityIssueRow extends ProductSecurityIssue {
  kind: string;
  severityLabel: string;
}

export interface SecurityMetric extends HealthMetric {
  total: number;
  checks: { name: string; issues: number }[];
  issues: SecurityIssueRow[];
  lastScannedAt: string | null; // null when Aikido holds the repository but has never scanned it
}

// One graded package shown in the Dependencies card's list.
export interface DependencyPackage {
  name: string;
  versionInUse: string;
  latestVersion: string;
  status: string; // the fact it was graded on, e.g. "1 major behind", "no 18.x release for 2 years"
  newerOutFor: string; // time since the first newer release came out, e.g. "9 days"; empty when there is none
  usedAt: string; // "used at 2 versions" when the product declares it at several; empty otherwise
  currency: 'unsupported' | 'behind' | 'current'; // the donut group it counts in, and its colour
}

// Support counts + every graded package for the Dependencies card.
export interface DependenciesMetric {
  status: HealthStatus; // colour of the card, from the shares in thresholds.ts
  packagesUsed: number; // packages that could be graded
  current: number; // on the latest release, ahead of it, or only a patch behind
  behind: number; // a newer stable release exists, still supported
  unsupported: number; // deprecated, or a newer major out a year and no release on this line for a year
  packages: DependencyPackage[]; // every graded package, unsupported first
  checkedAt: string | null; // the oldest registry read behind these figures
  uncheckedCount: number; // direct packages that could not be checked
}

// One chapter's (role's) values across the sprint series — a single stacked segment / legend row.
export interface ChapterSeries {
  key: string; // chapter key, e.g. 'BE' — stable identity that its colour follows
  label: string; // human label, e.g. "Back-end"
  values: number[]; // per sprint, aligned to periodLabels
}

// ---- Team — who worked on the product and for how long. Built in `team.ts` from the reported-time
// API. Hours only: a per-person rate and cost belong here when a financial epic decides who sees one. ----

export interface TeamMember {
  name: string;
  hours: number; // hours they reported on this product in the window
}

export interface TeamRole {
  key: string; // chapter key, e.g. 'BE' — its colour follows this
  label: string; // human role label, e.g. "Back-end"
  members: TeamMember[];
  hours: number; // total hours across the role's members
}

export interface TeamData {
  roles: TeamRole[];
  totalHours: number;
  headcount: number;
}
